import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import useApiData from '../../services/useApiData';
import GetData from '../../services/GetData';
import PostData from '../../services/PostData';

const WEIGHT_TOOLTIP = 'Round Up to Nearest Pound';
const LENGTH_TOOLTIP = "Measure along Box's Tape, Round Up to Nearest Inch";
const WIDTH_TOOLTIP = "Measure against Box's Tape, Round Up to Nearest Inch";
const HEIGHT_TOOLTIP = 'Measure dimension without opening, Round Up to Nearest Inch';

const IDENTITY_FIELDS = [
    { key: 'Year (Approx)', label: 'Year (Approx)' },
    { key: 'Box Value', label: 'Box Value' },
    { key: 'MPN', label: 'MPN' },
    { key: 'UPC', label: 'UPC' },
];

const RETAIL_METRIC_FIELDS = [
    { key: 'Retail - Weight', label: 'Retail Weight', tooltip: WEIGHT_TOOLTIP },
    { key: 'Retail - Length', label: 'Retail Length', tooltip: LENGTH_TOOLTIP },
    { key: 'Retail - Width', label: 'Retail Width', tooltip: WIDTH_TOOLTIP },
    { key: 'Retail - Height', label: 'Retail Height', tooltip: HEIGHT_TOOLTIP },
];

const PACK_WEIGHT_CONFIG_FIELDS = [
    { weightKey: 'Single - Weight', configKey: 'Single - Config', label: 'Single' },
    { weightKey: 'Double - Weight', configKey: 'Double - Config', label: 'Double' },
    { weightKey: '3x - Weight', configKey: '3x - Config', label: '3x' },
    { weightKey: '4x - Weight', configKey: '4x - Config', label: '4x' },
    { weightKey: 'Single BIAB - Weight', configKey: 'Single BIAB - Config', label: 'Single BIAB' },
    { weightKey: 'Double BIAB - Weight', configKey: 'Double BIAB - Config', label: 'Double BIAB' },
    { weightKey: '3x BIAB - Weight', configKey: '3x BIAB - Config', label: '3x BIAB' },
    { weightKey: '4x BIAB - Weight', configKey: '4x BIAB - Config', label: '4x BIAB' },
];

const emptyForm = () => ({
    product: null,
    'Year (Approx)': '',
    'Box Value': '',
    MPN: '',
    UPC: '',
    'Retail - Weight': '',
    'Retail - Length': '',
    'Retail - Width': '',
    'Retail - Height': '',
    'Single - Weight': '',
    'Single - Config': null,
    'Double - Weight': '',
    'Double - Config': null,
    '3x - Weight': '',
    '3x - Config': null,
    '4x - Weight': '',
    '4x - Config': null,
    'Single BIAB - Weight': '',
    'Single BIAB - Config': null,
    'Double BIAB - Weight': '',
    'Double BIAB - Config': null,
    '3x BIAB - Weight': '',
    '3x BIAB - Config': null,
    '4x BIAB - Weight': '',
    '4x BIAB - Config': null,
});

const findConfigOption = (options, recordId) =>
    (options ?? []).find((o) => o.recordId === recordId) ?? null;

const applyDefaultsToForm = (prev, defaults, boxConfigOptions, { onlyEmpty = true } = {}) => {
    const next = { ...prev };
    for (const field of [
        ...IDENTITY_FIELDS.map((f) => f.key),
        ...RETAIL_METRIC_FIELDS.map((f) => f.key),
        ...PACK_WEIGHT_CONFIG_FIELDS.map((f) => f.weightKey),
    ]) {
        if (
            defaults[field] != null &&
            defaults[field] !== '' &&
            (!onlyEmpty || prev[field] === '' || prev[field] == null)
        ) {
            next[field] = String(defaults[field]);
        }
    }
    for (const { configKey } of PACK_WEIGHT_CONFIG_FIELDS) {
        if (defaults[configKey] && (!onlyEmpty || !prev[configKey])) {
            next[configKey] = findConfigOption(boxConfigOptions, defaults[configKey]);
        }
    }
    return next;
};

const applyLoadedFieldsToForm = (product, fields, boxConfigOptions) => {
    const next = emptyForm();
    next.product = product ?? null;
    return applyDefaultsToForm(next, fields ?? {}, boxConfigOptions, { onlyEmpty: false });
};

const pickConfigIds = (fields) => {
    const ids = {};
    for (const { configKey } of PACK_WEIGHT_CONFIG_FIELDS) {
        const value = fields?.[configKey];
        if (value) ids[configKey] = value;
    }
    return ids;
};

export default function AddVariantDialog({
    close,
    onSaved,
    itemSku,
    mode = 'create',
    variantRecordId = null,
    cloneFromRecordId = null,
}) {
    const isEdit = mode === 'edit';
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [loadingForm, setLoadingForm] = useState(true);
    const [loadingDefaults, setLoadingDefaults] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [prefillNote, setPrefillNote] = useState('');
    const [pendingProductId, setPendingProductId] = useState(null);
    const [rawConfigIds, setRawConfigIds] = useState(null);
    const createPrefilledRef = useRef(false);

    const { data: skuList, load: fetchingSkus } = useApiData('api/broylesllc/sku-list-records');
    const { data: boxConfigData, load: fetchingBoxConfigs } = useApiData(
        'api/broylesllc/shipping/box-configs'
    );
    const boxConfigOptions = useMemo(
        () => boxConfigData?.options ?? [],
        [boxConfigData?.options]
    );
    const productId = form.product?.id ?? null;
    const yearApprox = form['Year (Approx)'];
    const boxValue = form['Box Value'];

    // Load once when dialog opens — do not re-run when skuList/box configs finish loading.
    useEffect(() => {
        let cancelled = false;

        const loadForm = async () => {
            setLoadingForm(true);
            setErrorMessage('');
            setPrefillNote('');
            setPendingProductId(null);
            setRawConfigIds(null);
            createPrefilledRef.current = false;

            if (isEdit && variantRecordId) {
                const { getData } = await GetData(
                    `broylesllc/shipping/sku-variant?recordId=${encodeURIComponent(variantRecordId)}`
                );
                if (cancelled) return;
                if (!getData?.found) {
                    setLoadingForm(false);
                    setErrorMessage('Could not load variant for editing.');
                    return;
                }
                setRawConfigIds(pickConfigIds(getData.fields));
                setForm(applyLoadedFieldsToForm(getData.product, getData.fields, []));
                setLoadingForm(false);
                return;
            }

            let nextForm = emptyForm();

            if (itemSku) {
                const { getData: productData } = await GetData(
                    `broylesllc/shipping/product-by-sku?itemSku=${encodeURIComponent(itemSku)}`
                );
                if (cancelled) return;

                if (productData?.found && productData.product?.recordId) {
                    setPendingProductId(productData.product.recordId);
                    nextForm.product = {
                        id: productData.product.recordId,
                        fields: {
                            'Listing Field': productData.product.listingField,
                        },
                    };
                    if (productData.product.upc) {
                        nextForm.UPC = String(productData.product.upc);
                    }
                    setPrefillNote(`Product pre-filled from order SKU (${itemSku}).`);
                }
            }

            if (cloneFromRecordId && nextForm.product?.id) {
                const params = new URLSearchParams({
                    productRecordId: nextForm.product.id,
                    sourceRecordId: cloneFromRecordId,
                    excludeYear: 'true',
                });
                const { getData: defaultsData } = await GetData(
                    `broylesllc/shipping/variant-defaults?${params.toString()}`
                );
                if (cancelled) return;
                if (defaultsData?.defaults) {
                    setRawConfigIds(pickConfigIds(defaultsData.defaults));
                    nextForm = applyDefaultsToForm(nextForm, defaultsData.defaults, [], {
                        onlyEmpty: true,
                    });
                    createPrefilledRef.current = true;
                    setPrefillNote((prev) =>
                        `${prev ? `${prev} ` : ''}Metrics copied from "${defaultsData.sourceLabel}". Enter the new year (e.g. 2026).`
                    );
                }
            }

            setForm(nextForm);
            setLoadingForm(false);
        };

        loadForm();

        return () => {
            cancelled = true;
        };
    }, [isEdit, variantRecordId, cloneFromRecordId, itemSku]);

    // Resolve config autocompletes once box options load (no re-fetch).
    useEffect(() => {
        if (!rawConfigIds || boxConfigOptions.length === 0) return;

        setForm((prev) => {
            let changed = false;
            const next = { ...prev };
            for (const { configKey } of PACK_WEIGHT_CONFIG_FIELDS) {
                const recordId = rawConfigIds[configKey];
                if (!recordId) continue;
                const option = findConfigOption(boxConfigOptions, recordId);
                if (option && prev[configKey]?.recordId !== recordId) {
                    next[configKey] = option;
                    changed = true;
                }
            }
            return changed ? next : prev;
        });
    }, [rawConfigIds, boxConfigOptions]);

    // Upgrade stub product object when sku list finishes loading.
    useEffect(() => {
        if (isEdit || !pendingProductId || !skuList?.length) return;

        const matched = skuList.find((p) => p.id === pendingProductId);
        if (!matched) return;

        setForm((prev) => {
            if (prev.product?.id !== pendingProductId) return prev;
            if (prev.product?.fields?.['Listing Field'] === matched.fields?.['Listing Field']) {
                return prev;
            }
            return { ...prev, product: matched };
        });
    }, [isEdit, pendingProductId, skuList]);

    // Create-only: debounced defaults when product/year/box change (skip edit + clone prefill).
    useEffect(() => {
        if (isEdit || loadingForm || !productId || createPrefilledRef.current) return;

        let cancelled = false;
        const timer = setTimeout(async () => {
            setLoadingDefaults(true);
            const params = new URLSearchParams({ productRecordId: productId });
            if (String(yearApprox ?? '').trim()) {
                params.set('yearApprox', String(yearApprox).trim());
            }
            if (String(boxValue ?? '').trim()) {
                params.set('boxValue', String(boxValue).trim());
            }

            const { getData } = await GetData(
                `broylesllc/shipping/variant-defaults?${params.toString()}`
            );
            if (cancelled) return;
            setLoadingDefaults(false);

            const defaults = getData?.defaults;
            if (!defaults || typeof defaults !== 'object') return;

            setRawConfigIds((prev) => ({ ...prev, ...pickConfigIds(defaults) }));
            setForm((prev) => {
                if (prev.product?.id !== productId) return prev;
                return applyDefaultsToForm(prev, defaults, boxConfigOptions, { onlyEmpty: true });
            });
        }, 350);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [isEdit, loadingForm, productId, yearApprox, boxValue, boxConfigOptions]);

    const setField = (key) => (e) => {
        setForm((prev) => ({ ...prev, [key]: e.target.value }));
    };

    const buildPayload = () => {
        const payload = {
            productRecordId: form.product?.id ?? null,
            'Year (Approx)': form['Year (Approx)'],
            'Box Value': form['Box Value'],
            MPN: form.MPN,
            UPC: form.UPC,
        };

        for (const { key } of RETAIL_METRIC_FIELDS) {
            payload[key] = form[key];
        }
        for (const { weightKey, configKey } of PACK_WEIGHT_CONFIG_FIELDS) {
            payload[weightKey] = form[weightKey];
            payload[configKey] = form[configKey]?.recordId ?? null;
        }

        if (isEdit) {
            payload.recordId = variantRecordId;
        }

        return payload;
    };

    const handleSave = async () => {
        if (!form.product?.id) {
            setErrorMessage('Product is required.');
            return;
        }

        setSaving(true);
        setErrorMessage('');

        const url = isEdit
            ? 'broylesllc/shipping/update-sku-variant'
            : 'broylesllc/shipping/create-sku-variant';
        const response = await PostData(buildPayload(), url, null);
        setSaving(false);

        if (response.success && response.data?.label) {
            onSaved?.(response.data.label, isEdit ? 'updated' : 'created');
            close();
            return;
        }

        const msg =
            response.data?.message ||
            response.error?.response?.data?.message ||
            response.error?.message ||
            `Failed to ${isEdit ? 'update' : 'create'} variant`;
        setErrorMessage(msg);
    };

    return (
        <>
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pr: 1,
                }}
            >
                {isEdit ? 'Edit Product Variant' : 'Add Product Variant'}
                <IconButton onClick={close} size="small" aria-label="close">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {loadingForm ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={32} />
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 0.5 }}>
                        {prefillNote && (
                            <Typography variant="body2" color="text.secondary">
                                {prefillNote}
                            </Typography>
                        )}

                        <Autocomplete
                            loading={fetchingSkus}
                            disabled={isEdit}
                            value={form.product}
                            onChange={(_, newValue) => {
                                createPrefilledRef.current = false;
                                setRawConfigIds(null);
                                setForm((prev) => ({ ...emptyForm(), product: newValue }));
                            }}
                            options={skuList ?? []}
                            getOptionLabel={(option) => option?.fields?.['Listing Field'] || ''}
                            isOptionEqualToValue={(option, value) => option?.id === value?.id}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Product"
                                    required
                                    size="small"
                                    variant="outlined"
                                    helperText={
                                        isEdit
                                            ? 'Product cannot be changed when editing.'
                                            : itemSku
                                                ? `Matched from order SKU: ${itemSku}`
                                                : undefined
                                    }
                                />
                            )}
                        />

                        {IDENTITY_FIELDS.map(({ key, label }) => (
                            <TextField
                                key={key}
                                label={label}
                                value={form[key]}
                                onChange={setField(key)}
                                size="small"
                                fullWidth
                                variant="outlined"
                            />
                        ))}

                        {loadingDefaults && !isEdit && (
                            <Typography variant="body2" color="text.secondary">
                                Looking up existing metrics to pre-populate...
                            </Typography>
                        )}

                        <Typography variant="subtitle2" sx={{ mt: 1 }}>
                            Retail dimensions
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                            {RETAIL_METRIC_FIELDS.map(({ key, label, tooltip }) => (
                                <Box key={key} sx={{ flex: '1 1 140px' }}>
                                    <Tooltip title={tooltip} arrow placement="top">
                                        <TextField
                                            fullWidth
                                            label={label}
                                            type="number"
                                            value={form[key]}
                                            onChange={setField(key)}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </Tooltip>
                                </Box>
                            ))}
                        </Box>

                        <Typography variant="subtitle2" sx={{ mt: 1 }}>
                            Pack weights & configs
                        </Typography>
                        {PACK_WEIGHT_CONFIG_FIELDS.map(({ weightKey, configKey, label }) => (
                            <Box
                                key={label}
                                sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}
                            >
                                <Box sx={{ flex: '1 1 140px' }}>
                                    <Tooltip title={WEIGHT_TOOLTIP} arrow placement="top">
                                        <TextField
                                            fullWidth
                                            label={`${label} Weight`}
                                            type="number"
                                            value={form[weightKey]}
                                            onChange={setField(weightKey)}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </Tooltip>
                                </Box>
                                <Box sx={{ flex: '2 1 220px' }}>
                                    <Autocomplete
                                        loading={fetchingBoxConfigs}
                                        value={form[configKey]}
                                        onChange={(_, newValue) => {
                                            setForm((prev) => ({ ...prev, [configKey]: newValue }));
                                        }}
                                        options={boxConfigOptions}
                                        getOptionLabel={(option) => option?.label || ''}
                                        isOptionEqualToValue={(option, value) =>
                                            option?.recordId === value?.recordId
                                        }
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={`${label} Config`}
                                                size="small"
                                                variant="outlined"
                                            />
                                        )}
                                    />
                                </Box>
                            </Box>
                        ))}

                        {errorMessage && (
                            <Typography color="error" variant="body2">
                                {errorMessage}
                            </Typography>
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={close} disabled={saving} sx={{ textTransform: 'none' }}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    disableElevation
                    onClick={handleSave}
                    disabled={saving || loadingForm || !form.product?.id}
                    sx={{ textTransform: 'none', background: '#FF8C61' }}
                >
                    {saving ? (
                        <CircularProgress size={18} color="inherit" />
                    ) : isEdit ? (
                        'Save changes'
                    ) : (
                        'Save variant'
                    )}
                </Button>
            </DialogActions>
        </>
    );
}
